import type { Booking } from '@/types';
import { parseStoredBookingTime, quantizeResizeDelta } from '@/lib/groupResize';

export interface GroupDragTarget {
  booking: Booking;
  newStartTime: Date;
  newEndTime: Date;
}

/**
 * The row a drop lands on. A multi-selection spans rows, so the rows its bookings sit on
 * are kept and the drop only moves them in time; a split booking's pieces share one row
 * and follow the drag onto the row they were dropped on.
 */
export function dropResourceId({
  isMultiSelectionDrag,
  isSplitGroupDrag,
  draggedResourceId,
  droppedResourceId,
}: {
  isMultiSelectionDrag: boolean;
  isSplitGroupDrag: boolean;
  draggedResourceId: string;
  droppedResourceId: string;
}): string {
  return isMultiSelectionDrag && !isSplitGroupDrag ? draggedResourceId : droppedResourceId;
}

/**
 * Whether a drop must leave the booking on the resources it is saved on, read from all of
 * its pieces. A booking assigned to several resources has a tile on each of their rows, so
 * the row a drop lands on says nothing about which resource the booking belongs to — taking
 * it as the destination would collapse the booking onto that one resource and unassign the
 * rest. A day only some of those resources work is booked for those alone, on a piece of its
 * own, so a piece of a booking on several resources can well name a single one.
 */
export function groupDropKeepsResources(
  pieces: readonly Pick<Booking, 'resourceIds' | 'resourceId'>[],
): boolean {
  const resourceIds = new Set<string>();

  for (const piece of pieces) {
    const held = piece.resourceIds?.length
      ? piece.resourceIds
      : (piece.resourceId != null ? [piece.resourceId] : []);
    for (const id of held) {
      if (String(id) !== '') resourceIds.add(String(id));
    }
  }

  return resourceIds.size > 1;
}

export function groupDragTargets({
  dragged,
  selection,
  correctedStart,
  movesResource = false,
}: {
  dragged: Booking;
  selection: Booking[];
  correctedStart: Date;
  /** Whether the drop also puts the bookings on another resource. */
  movesResource?: boolean;
}): { targets: GroupDragTarget[]; skipped: Booking[] } {
  const draggedStart = parseStoredBookingTime(dragged.startTime);
  const delta = quantizeResizeDelta(correctedStart.getTime() - draggedStart.getTime());
  const orderedSelection = [
    dragged,
    ...selection.filter(booking => String(booking.id) !== String(dragged.id)),
  ];

  if (dragged.recurrenceGroupId) {
    return { targets: [], skipped: orderedSelection };
  }

  // A drop onto another resource is a move even when it lands on the same dates.
  if (!Number.isFinite(delta) || (delta === 0 && !movesResource)) {
    return Number.isFinite(delta)
      ? { targets: [], skipped: [] }
      : { targets: [], skipped: orderedSelection };
  }

  const targets: GroupDragTarget[] = [];
  const skipped: Booking[] = [];

  for (const booking of orderedSelection) {
    if (booking.recurrenceGroupId) {
      skipped.push(booking);
      continue;
    }

    const originalStart = parseStoredBookingTime(booking.startTime);
    const originalEnd = parseStoredBookingTime(booking.endTime);
    if (!Number.isFinite(originalStart.getTime()) || !Number.isFinite(originalEnd.getTime())) {
      skipped.push(booking);
      continue;
    }

    targets.push({
      booking,
      newStartTime: new Date(originalStart.getTime() + delta),
      newEndTime: new Date(originalEnd.getTime() + delta),
    });
  }

  return { targets, skipped };
}
